// Global
import React from 'react';
import { Link, LinkProps, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';

// Lib
import HTMLComment from 'react-html-comment';
import useExperienceEditor from 'lib/use-experience-editor';
import { hostToReplace } from 'lib/host-to-replace';
import classNames from 'classnames';

/**
 * This component adds some needed accessibility updates to the JSS Link component
 */
export interface LinkWrapperProps extends LinkProps {
  srOnlyText?: string;
  suppressLinkText?: boolean;
  suppressNewTabIcon?: boolean;
}

const INTERNAL_LINK_REGEX = /^zzzzzzzz\/|^\#/g;

const LinkWrapper = React.forwardRef(
  ({
    children,
    field,
    srOnlyText,
    suppressLinkText,
    suppressNewTabIcon,
    ref,
    className,
    ...props
  }: LinkWrapperProps): // _ref
  JSX.Element => {
    // Format field as LinkField for consistency
    const asLinkField = !field?.value ? { value: { ...field } } : (field as LinkField | undefined);
    const text = suppressLinkText || children ? '' : asLinkField?.value?.text;
    const target = asLinkField?.value?.target;

    // Combine class of Link Wrapper attribute and Layout Link Field Class
    const combineClass = classNames(asLinkField?.value?.class, className);

    const isLangLink = combineClass?.includes('langLink');

    const processedField = asLinkField ? hostToReplace(asLinkField) : undefined;

    const isEE = useExperienceEditor();

    const isInternalLink = processedField?.value?.linktype === 'internal';
    const isAnchorLink = processedField?.value?.linktype === 'anchor';

    const linkHref = isInternalLink
      ? processedField?.value?.href.replace(/^[^?#]*/, (path) => path.toLowerCase())
      : processedField?.value?.href;

    const ariaLabel = processedField?.value?.title
      ? processedField?.value?.title
      : processedField?.value?.text;

    // In experience editor, do not pass any children but retain basic styling so that double components do not appear when using <Link>
    if (isEE) {
      return (
        <Link
          field={processedField as LinkField}
          showLinkTextWithChildrenPresent={false}
          internalLinkMatcher={INTERNAL_LINK_REGEX}
          ref={typeof ref !== 'string' ? ref : null}
          className={combineClass}
          {...props}
          href={processedField?.value?.href}
        >
          {children}
        </Link>
      );
    }

    // If no content is present, don't print
    if (!suppressLinkText && !asLinkField?.value.text && !asLinkField?.value.href) return <></>;

    return (
      <>
        {isLangLink && <HTMLComment text="mp_global_switch_begins" />}
        <Link
          field={processedField as LinkField}
          {...(!isAnchorLink && { internalLinkMatcher: INTERNAL_LINK_REGEX })}
          ref={typeof ref !== 'string' ? ref : null}
          showLinkTextWithChildrenPresent={false}
          className={combineClass}
          {...props}
          href={linkHref}
          data-href={linkHref}
          aria-label={ariaLabel}
          aria-describedby={ariaLabel}
          {...(isLangLink && { 'data-lang': 'es' })} // Add data-lang only if isLangLink is true
        >
          {text}
          {children}
          {(target === '_blank' || srOnlyText) && (
            <>
              <span className="sr-only">
                {srOnlyText}
                {/* Preserve a single space character before SR Tab Text */}
                {target === '_blank' && ' (Opens in a new tab)'}
              </span>
              {/* Icon Goes Here */}
              {!suppressNewTabIcon && target === '_blank' && <span></span>}
            </>
          )}
        </Link>
        {isLangLink && <HTMLComment text="mp_global_switch_ends" />}
      </>
    );
  }
);

LinkWrapper.displayName = 'LinkWrapper';

export default LinkWrapper;
